import React, {
  Suspense,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { getDataFromHomePageApi, type ApiComponent } from "../../lib/api";
import PreLoader from "../../components/preloader/PreLoader";

// Lazy import map
const LAZY_COMPONENTS: Record<
  string,
  () => Promise<{ default: React.ComponentType<any> }>
> = {
  BannerServices: () =>
    import("../../components/bannerServices/BannerServicesNew"),
  VisasuggestCarousel: () =>
    import("../../components/homeNew/VisasuggestCarousel"),
  CustomerHappiness: () =>
    import("../../components/homeNew/CustomerHappinessCards"),
  PromoBanner: () => import("../../components/homeNew/PromoBanner"),
  NewServices: () => import("../../components/homeNew/NwServices"),
  Employees: () => import("../../pages/Department/AllSubComponents/Employees"),
  TrustedFactor: () =>
    import("../../pages/Department/AllSubComponents/TrustedFactor"),
  Certificate: () => import("../../components/Certificates/Certificate"),
  ServiceEmirate: () =>
    import("../../pages/Department/AllSubComponents/DepartmentEmirate"),
  WhyChooseUs: () =>
    import("../../pages/Department/AllSubComponents/WhyChooseUs"),
  AuthorizedPartner: () =>
    import("../../pages/Department/AllSubComponents/AuthorizedPartner"),
};

// Wrapper for lazy loading only when visible, and keep it mounted
const LazyBlock: React.FC<{
  loader: () => Promise<{ default: React.ComponentType<any> }>;
  props?: any;
}> = ({ loader, props }) => {
  const [visible, setVisible] = useState(false);
  const [loadedCmp, setLoadedCmp] = useState<React.ComponentType<any> | null>(
    null
  );
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current || loadedCmp) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const LazyCmp = React.lazy(loader);
          setLoadedCmp(() => LazyCmp); // save loaded component
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "1000px" }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [loader, loadedCmp]);

  // Before visible, show placeholder with height
  if (!visible && !loadedCmp)
    return <div ref={ref} style={{ minHeight: "250px" }} />;

  const Cmp = loadedCmp!;
  return (
    <div ref={ref}>
      <Suspense fallback={<PreLoader />}>
        <Cmp {...props} />
      </Suspense>
    </div>
  );
};

interface HomeNewProps {
  pageUrl?: string;
}

const HomeNew: React.FC<HomeNewProps> = ({ pageUrl }) => {
  const [items, setItems] = useState<ApiComponent[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const urlParam = useMemo(() => {
    const raw =
      pageUrl ??
      (typeof window !== "undefined" ? window.location.pathname : "/");
    return raw || "/";
  }, [pageUrl]);

  useEffect(() => {
    Object.values(LAZY_COMPONENTS).forEach((loader) => loader());
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setErr(null);

      try {
        const components = await getDataFromHomePageApi(controller.signal);

        if (!components) {
          setItems([]);
          return;
        }
        setItems(components);
      } catch (e: any) {
        if (e?.name !== "CanceledError") {
          setErr(e?.message || "Failed to load");
        }
      } 
      finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [urlParam]);

  if (loading && !items) return <PreLoader />;
  if (err) return <div style={{ color: "crimson" }}>Error: {err}</div>;

  return (
    <>
      {items &&
        items.map((block, idx) => {
          const loader = LAZY_COMPONENTS[block.name];
          if (!loader) {
            return null;
          }

          return (
            <div className="HomeNew" key={`${block.name}-${idx}`}>
              <LazyBlock loader={loader} props={block.props} />
            </div>
          );
        })}
    </>
  );
};

export default HomeNew;